import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import ReportsMenuStructure from "../../menuStructures/ReportsMenuStructure";
import SetActiveMenu from "../../menuStructures/SetActiveMenu";
import {
  useDataExportHistory,
  useDataExportJobPoller,
  useDataExportRegistry,
  useTriggerDataExport,
  useCancelDataExportJob,
} from "../../hooks/dataExportHooks";
import type {
  DataExportHistoryItem,
  DataExportJobStatus,
  DataExportRegistryItem,
} from "../../types/dataExport";
import ExportTypeMultiselect, {
  isFullAccountExport,
} from "../../components/reports/ExportTypeMultiselect";
import DataExportDateDropdown from "../../components/reports/DataExportDateDropdown";
import ButtonComponent from "../../components/generic/ButtonComponent";
import Modal from "../../components/generic/Modal";
import "./DataExports.scss";
import {
  exportLabelForId,
  exportSubmitButtonLabel,
  exportTypeLabel,
  resolveBatchSizeLimits,
  summarizeBatchExportResult,
} from "../../constants/dataExports";
import type { ExportDateFieldValue } from "../../helpers/dataExportDateHelpers";
import {
  DEFAULT_FROM_VALUE,
  DEFAULT_TO_VALUE,
  FROM_DATE_OPTIONS,
  TO_DATE_OPTIONS,
  displayLabelForField,
  resolveApiDate,
  todayApiDate,
} from "../../helpers/dataExportDateHelpers";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

// The all-account ("All Data") export ignores any date range on the backend, so
// its date controls stay greyed out even if the registry flags it otherwise.
const descriptorSupportsDateRange = (item: DataExportRegistryItem): boolean =>
  item.supportsDateRange && !isFullAccountExport(item);

const STATUS_LABEL: Record<string, string> = {
  queued: "🕒 Queued",
  processing: "⏳ Processing",
  ready: "✅ Ready",
  streamed: "⬇ Streamed",
  error: "❌ Failed",
  cancelled: "🚫 Cancelled",
};

/** Set true to show batch size input for cascade exports that support it. */
const SHOW_BATCH_SIZE_CONTROL = false;

const STATUS_CLASS: Record<string, string> = {
  queued: "",
  processing: "",
  ready: "",
  streamed: "",
  error: "",
  cancelled: "",
};

/**
 * Downloads a remote file with a specific filename.
 * Strategy 1: fetch as blob → create object URL → anchor download (gives full
 *   control over filename; works when GCS bucket has CORS configured).
 * Strategy 2 (fallback): direct anchor click with download attribute — some
 *   browsers honour this even for cross-origin pre-signed URLs.
 */
async function downloadFile(url: string, filename: string): Promise<void> {
  // Strategy 1 — blob fetch (controls filename reliably)
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    return;
  } catch {
    // CORS or network error — fall through to strategy 2
  }

  // Strategy 2 — anchor with download attribute
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function formatDate(isoString: string | null): string {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

type DownloadCellProps = {
  item: DataExportHistoryItem;
};

function DownloadCell({ item }: DownloadCellProps) {
  const cell: React.CSSProperties = { fontSize: "0.8em" };
  const link: React.CSSProperties = {
    ...cell,
    background: "none",
    border: "none",
    padding: 0,
    color: "#007bff",
    cursor: "pointer",
    textDecoration: "underline",
    fontFamily: "inherit",
  };

  if (item.status === "error") {
    return (
      <span style={{ ...cell, color: "#dc3545" }}>
        {item.errorMessage ?? "Error"}
      </span>
    );
  }
  if (item.status === "streamed") {
    return (
      <span style={{ ...cell, color: "#6c757d" }}>Downloaded directly</span>
    );
  }
  if (item.status === "processing") {
    return <span style={{ ...cell, color: "#6c757d" }}>&mdash;</span>;
  }
  if (item.status === "ready" && !item.downloadUrls?.length) {
    return (
      <span style={{ ...cell, color: "#6c757d" }}>
        No data found for this export
      </span>
    );
  }
  if (!item.downloadUrls?.length) {
    return <span style={{ ...cell, color: "#6c757d" }}>&mdash;</span>;
  }

  // Derive consistent names from export label + completed date
  const count = item.downloadUrls.length;
  const safeName = (
    item.filename ?? exportLabelForId(item.exportId, item.label)
  )
    .replace(/[/\\:*?"<>|]/g, "")
    .trim();
  const completedDate = item.updatedAt
    ? (() => {
        const d = new Date(item.updatedAt as string);
        const pad = (n: number) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
          d.getDate()
        )} ${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
      })()
    : "";
  const dateSuffix = completedDate ? ` ${completedDate}` : "";

  // Derive the real file extension from the URL (strip query params first).
  // Handles both .csv (individual exports) and .zip (encrypted / "Download All").
  // Falls back to .csv if the URL has no recognisable extension.
  const urlExt = (url: string): string => {
    try {
      const path = url.split("?")[0];
      const lastSegment = path.split("/").pop() ?? "";
      const dot = lastSegment.lastIndexOf(".");
      if (dot > 0) return lastSegment.slice(dot); // e.g. ".zip" or ".csv"
    } catch {
      // ignore
    }
    return ".csv";
  };

  // Display name (UI link text): no extension — clean and format-agnostic
  const displayName = (i: number) =>
    count > 1 ? `${safeName} Part${i + 1}` : safeName;

  // Download filename: includes timestamp + correct extension so the OS opens
  // the file with the right application
  const downloadName = (i: number) =>
    count > 1
      ? `${safeName} Part${i + 1}${dateSuffix}${urlExt(item.downloadUrls[i])}`
      : `${safeName}${dateSuffix}${urlExt(item.downloadUrls[i])}`;

  return (
    <div>
      {count > 1 && (
        <div style={{ ...cell, color: "#6c757d", marginBottom: 2 }}>
          {count} files
        </div>
      )}
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {item.downloadUrls.map((url, i) => (
          <li key={url} style={{ marginBottom: 1 }}>
            <button
              style={link}
              onClick={() => downloadFile(url, downloadName(i))}
            >
              &#x2193; {displayName(i)}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

function DataExports() {
  useEffect(() => {
    document.title = "Data Exports | BinderPOS";
  }, []);

  // ── Registry + history ────────────────────────────────────────────────────
  const { data: registry = [], isLoading: registryLoading } =
    useDataExportRegistry();
  const {
    data: history = [],
    isLoading: historyLoading,
    refetch: refetchHistory,
  } = useDataExportHistory();

  // ── Form state ────────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deliveryMode, setDeliveryMode] = useState<"sync" | "async">("sync");
  const [fromDate, setFromDate] =
    useState<ExportDateFieldValue>(DEFAULT_FROM_VALUE);
  const [toDate, setToDate] = useState<ExportDateFieldValue>(DEFAULT_TO_VALUE);
  const [batchSize, setBatchSize] = useState("");

  const [progressModalVisible, setProgressModalVisible] = useState(false);
  const [progressSummary, setProgressSummary] = useState("");
  const [progressJobIds, setProgressJobIds] = useState<number[]>([]);
  const [progressDateApplicableNames, setProgressDateApplicableNames] =
    useState<string[]>([]);
  const [progressAllDataNames, setProgressAllDataNames] = useState<string[]>(
    []
  );

  // Active jobs being polled (jobId → label)
  const [activePolls, setActivePolls] = useState<Map<number, string>>(
    new Map()
  );

  const selectedDescriptors: DataExportRegistryItem[] = useMemo(
    () =>
      selectedIds
        .map((id) => registry.find((r) => r.id === id))
        .filter((item): item is DataExportRegistryItem => Boolean(item)),
    [registry, selectedIds]
  );

  const batchSizeLimits = useMemo(
    () =>
      SHOW_BATCH_SIZE_CONTROL
        ? resolveBatchSizeLimits(selectedDescriptors)
        : null,
    [selectedDescriptors]
  );

  // Keep delivery/date/batch fields in sync with the current selection
  useEffect(() => {
    if (!selectedDescriptors.length) return;
    if (selectedDescriptors.every((item) => item.delivery === "async")) {
      setDeliveryMode("async");
    } else if (!selectedDescriptors.some((item) => item.delivery === "both")) {
      setDeliveryMode("sync");
    }
    if (!selectedDescriptors.some(descriptorSupportsDateRange)) {
      setFromDate(DEFAULT_FROM_VALUE);
      setToDate(DEFAULT_TO_VALUE);
    }
    if (
      !selectedDescriptors.some((item) => item.supportsBatchSize) ||
      (batchSizeLimits && !batchSizeLimits.hasCommonRange)
    ) {
      setBatchSize("");
    }
  }, [selectedDescriptors, batchSizeLimits]);

  // ── Polling ───────────────────────────────────────────────────────────────
  const onPollComplete = useCallback(
    (result: DataExportJobStatus) => {
      setActivePolls((prev) => {
        const next = new Map(prev);
        next.delete(result.jobId);
        return next;
      });
      refetchHistory();
    },
    [refetchHistory]
  );

  // We need one poller per active job — managed via a map of JobPollerRow components
  // (see JobPollerRow below)

  // ── Trigger export ────────────────────────────────────────────────────────
  const onAsyncQueued = useCallback(
    (jobId: number, label: string) => {
      setActivePolls((prev) => new Map(prev).set(jobId, label));
      setProgressJobIds((prev) => [...prev, jobId]);
      refetchHistory();
    },
    [refetchHistory]
  );

  const {
    trigger,
    loading: triggering,
    error: triggerError,
    clearError,
    setError,
  } = useTriggerDataExport(onAsyncQueued);

  const { cancel: cancelJob, cancellingIds } = useCancelDataExportJob();

  // Detect the moment the last running job finishes: activePolls going from
  // non-empty back to empty. We key off activePolls (not progressJobIds)
  // because a second submit while a job is running resets progressJobIds.
  const hadActiveJobsRef = useRef(false);
  useEffect(() => {
    if (activePolls.size > 0) {
      hadActiveJobsRef.current = true;
      return;
    }
    if (!hadActiveJobsRef.current) return;
    hadActiveJobsRef.current = false;

    // Every queued job has reached a terminal state (completed, cancelled or
    // errored): close the modal, drop the waiting/notice banners and reset the
    // form. The notice banner is selection-driven, so clearing the selection
    // removes it; setError(null) clears any lingering waiting/error banner.
    setProgressModalVisible(false);
    setSelectedIds([]);
    setFromDate(DEFAULT_FROM_VALUE);
    setToDate(DEFAULT_TO_VALUE);
    setBatchSize("");
    setError(null);
    setProgressJobIds([]);
    setProgressDateApplicableNames([]);
    setProgressAllDataNames([]);
  }, [activePolls, setError]);

  const handleCancel = useCallback(
    async (jobId: number) => {
      const ok = await cancelJob(jobId);
      if (ok) {
        setActivePolls((prev) => {
          const next = new Map(prev);
          next.delete(jobId);
          return next;
        });
      }
    },
    [cancelJob]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDescriptors.length) return;
    clearError();

    if (SHOW_BATCH_SIZE_CONTROL && batchSizeLimits && batchSize.trim()) {
      if (!batchSizeLimits.hasCommonRange) {
        toast.error(
          `${batchSizeLimits.labels.join(
            ", "
          )} have no batch size in common. Clear the batch size to use each export's default, or export them separately.`
        );
        return;
      }
      const parsed = Number(batchSize);
      const { min, max } = batchSizeLimits;
      if (!Number.isInteger(parsed) || parsed < min || parsed > max) {
        toast.error(
          `Batch size must be a whole number between ${min.toLocaleString()} and ${max.toLocaleString()}.`
        );
        return;
      }
    }

    const hasDateRangeExports = selectedDescriptors.some(
      descriptorSupportsDateRange
    );

    if (hasDateRangeExports) {
      const fromResolved = resolveApiDate(fromDate);
      const toResolved = resolveApiDate(toDate);
      if (
        fromResolved &&
        toResolved &&
        new Date(fromResolved) > new Date(toResolved)
      ) {
        toast.error("The From date must be on or before the To date.");
        return;
      }
    }

    const apiDateFrom = hasDateRangeExports
      ? resolveApiDate(fromDate)
      : undefined;
    const apiDateTo = hasDateRangeExports ? resolveApiDate(toDate) : undefined;

    const dateParams = {
      ...(apiDateFrom ? { dateFrom: apiDateFrom } : {}),
      ...(apiDateTo ? { dateTo: apiDateTo } : {}),
    };

    setProgressJobIds([]);
    setProgressSummary(buildRequestSummary());
    setProgressDateApplicableNames(
      selectedDescriptors
        .filter(descriptorSupportsDateRange)
        .map(exportTypeLabel)
    );
    setProgressAllDataNames(
      selectedDescriptors
        .filter((item) => !descriptorSupportsDateRange(item))
        .map(exportTypeLabel)
    );
    let startedCount = 0;
    let asyncQueuedCount = 0;
    const failedLabels: string[] = [];

    if (selectedDescriptors.length > 1) {
      // Multiple types → a single async job that returns one combined zip. The
      // backend applies the date range only to members that support it and
      // ignores it for the rest, matching the mixed-selection rule above.
      const result = await trigger(
        {
          exportIds: selectedDescriptors.map((descriptor) => descriptor.id),
          mode: "async",
          ...dateParams,
          ...(SHOW_BATCH_SIZE_CONTROL &&
          selectedDescriptors.some((item) => item.supportsBatchSize) &&
          batchSize.trim()
            ? { batchSize: Number(batchSize) }
            : {}),
        },
        selectedDescriptors.map(exportTypeLabel).join(", ")
      );
      if (result.ok) {
        startedCount = 1;
        asyncQueuedCount = 1;
      }
      // On failure the hook surfaces the backend message in the banner; leaving
      // failedLabels empty avoids overriding it with a generic batch summary.
    } else {
      const descriptor = selectedDescriptors[0];
      const mode =
        descriptor.delivery === "async"
          ? "async"
          : descriptor.delivery === "sync"
          ? "sync"
          : deliveryMode;
      const result = await trigger(
        {
          exportId: descriptor.id,
          mode,
          ...(descriptorSupportsDateRange(descriptor) ? dateParams : {}),
          ...(SHOW_BATCH_SIZE_CONTROL &&
          descriptor.supportsBatchSize &&
          batchSize.trim()
            ? { batchSize: Number(batchSize) }
            : {}),
        },
        exportTypeLabel(descriptor)
      );
      if (result.ok) {
        startedCount = 1;
        if (mode === "async") asyncQueuedCount = 1;
      } else {
        failedLabels.push(exportTypeLabel(descriptor));
      }
    }

    const batchSummary = summarizeBatchExportResult(
      startedCount,
      selectedDescriptors.length,
      failedLabels
    );

    // Only surface the "in progress" modal once an async job has actually been
    // queued. If nothing started (e.g. a duplicate job that's still waiting
    // behind one already in progress — shown via the banner) or every export
    // was a synchronous download that already completed above, keep it hidden.
    // Async jobs keep the modal open until their pollers finish (see effect).
    if (startedCount > 0 && asyncQueuedCount > 0) {
      setProgressModalVisible(true);
    } else {
      setProgressModalVisible(false);
    }

    if (batchSummary) {
      setError(batchSummary);
    }
  };

  const buildRequestSummary = (): string => {
    const dateApplicable = selectedDescriptors.filter(
      descriptorSupportsDateRange
    );
    const allDataOnly = selectedDescriptors.filter(
      (item) => !descriptorSupportsDateRange(item)
    );

    // No selected type uses the date range (e.g. All Account Data, or only
    // non-date exports) → just list the labels, no range shown.
    if (!dateApplicable.length) {
      return selectedDescriptors.map(exportTypeLabel).join(", ");
    }

    const from = displayLabelForField(fromDate, FROM_DATE_OPTIONS);
    const to = displayLabelForField(toDate, TO_DATE_OPTIONS);
    const datePart = `${dateApplicable
      .map(exportTypeLabel)
      .join(", ")} ${from} - ${to}`;

    // Mixed selection → the date range applies to the types above; the rest
    // export all data regardless of the range.
    if (allDataOnly.length) {
      return `${datePart}; ${allDataOnly
        .map(exportTypeLabel)
        .join(", ")} (all data)`;
    }

    return datePart;
  };

  // 2+ types are always sent as a single async bundle (one combined zip), so
  // the per-export delivery choice doesn't apply.
  const isBundle = selectedDescriptors.length > 1;
  const asyncOnlyDescriptors = selectedDescriptors.filter(
    (item) => item.delivery === "async"
  );
  const showDeliverySelect =
    !isBundle && selectedDescriptors.some((item) => item.delivery === "both");
  // Date fields are greyed out unless a selected export supports date ranges.
  const dateFieldsEnabled = selectedDescriptors.some(
    descriptorSupportsDateRange
  );

  // For mixed selections, spell out which exports honour the date range and
  // which return all data (mirrors the progress modal summary).
  const dateApplicableDescriptors = selectedDescriptors.filter(
    descriptorSupportsDateRange
  );
  const allDataDescriptors = selectedDescriptors.filter(
    (item) => !descriptorSupportsDateRange(item)
  );
  const showDateScopeNote =
    dateApplicableDescriptors.length > 0 && allDataDescriptors.length > 0;

  // Constrain the pickers so To can never be earlier than From, and neither can
  // be in the future (mirrors the native min/max + today cap used by
  // DateRangeSelect in the sales reports).
  const today = todayApiDate();
  const fromResolvedDate = resolveApiDate(fromDate);
  const toResolvedDate = resolveApiDate(toDate);
  const fromMaxDate =
    toResolvedDate && toResolvedDate < today ? toResolvedDate : today;
  const supportsBatchSize =
    SHOW_BATCH_SIZE_CONTROL &&
    selectedDescriptors.some((item) => item.supportsBatchSize);
  const usesSyncDownload =
    !isBundle &&
    selectedDescriptors.some((item) => {
      if (item.delivery === "sync") return true;
      if (item.delivery === "both") return deliveryMode === "sync";
      return false;
    });
  const usesAsyncQueue =
    isBundle ||
    selectedDescriptors.some((item) => {
      if (item.delivery === "async") return true;
      if (item.delivery === "both") return deliveryMode === "async";
      return false;
    });

  return (
    <Fragment>
      <SetActiveMenu menuStructure={ReportsMenuStructure} />

      <div className="app-header">
        <h1 className="title">
          <span className="header-text">Data Exports</span>
        </h1>
      </div>

      <div>
        {/* ── New Export panel ─────────────────────────────────────────── */}
        <div className="card mb-4">
          <div className="card-header fw-semibold">New Export</div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              {/* Controls flow left-to-right, fixed widths, uniform gap */}
              <div
                className="d-flex flex-wrap align-items-end"
                style={{ gap: "20px" }}
              >
                {/* Export type */}
                <div style={{ width: 260 }}>
                  <label
                    className="form-label mb-1 small text-uppercase text-muted"
                    style={{ letterSpacing: "0.04em", fontWeight: 600 }}
                  >
                    Export Type
                  </label>
                  {registryLoading ? (
                    <p className="text-muted small mb-0">Loading exports…</p>
                  ) : (
                    <ExportTypeMultiselect
                      registry={registry}
                      selectedIds={selectedIds}
                      onChange={(ids) => {
                        setSelectedIds(ids);
                        clearError();
                      }}
                    />
                  )}
                </div>

                {/* Delivery mode — only when any selected export supports both */}
                {showDeliverySelect && (
                  <div style={{ width: 210 }}>
                    <label
                      className="form-label mb-1 small text-uppercase text-muted"
                      style={{ letterSpacing: "0.04em", fontWeight: 600 }}
                    >
                      Delivery
                    </label>
                    <select
                      className="form-control form-control-sm"
                      style={{ height: 36 }}
                      value={deliveryMode}
                      onChange={(e) =>
                        setDeliveryMode(e.target.value as "sync" | "async")
                      }
                    >
                      <option value="sync">⚡ Download Now</option>
                      <option value="async">📬 Background</option>
                    </select>
                  </div>
                )}

                {/* Batch size — when enabled and supported by export type */}
                {supportsBatchSize && batchSizeLimits && (
                  <div style={{ width: 160 }}>
                    <label
                      className="form-label mb-1 small text-uppercase text-muted"
                      style={{ letterSpacing: "0.04em", fontWeight: 600 }}
                    >
                      {batchSizeLimits.hasCommonRange
                        ? `Batch Size (${batchSizeLimits.min.toLocaleString()} - ${batchSizeLimits.max.toLocaleString()})`
                        : "Batch Size"}
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      style={{ height: 36 }}
                      value={batchSize}
                      min={batchSizeLimits.min}
                      max={batchSizeLimits.max}
                      step={1}
                      disabled={!batchSizeLimits.hasCommonRange}
                      placeholder={
                        batchSizeLimits.hasCommonRange
                          ? String(batchSizeLimits.default)
                          : "Unavailable"
                      }
                      title={
                        batchSizeLimits.hasCommonRange
                          ? `Default ${batchSizeLimits.default.toLocaleString()}`
                          : "The selected exports have no batch size range in common — each will use its own default."
                      }
                      onChange={(e) => setBatchSize(e.target.value)}
                    />
                  </div>
                )}

                <DataExportDateDropdown
                  label="From Date"
                  options={FROM_DATE_OPTIONS}
                  value={fromDate}
                  disabled={!dateFieldsEnabled}
                  onChange={setFromDate}
                  width={200}
                  max={fromMaxDate}
                />
                <DataExportDateDropdown
                  label="To Date"
                  options={TO_DATE_OPTIONS}
                  value={toDate}
                  disabled={!dateFieldsEnabled}
                  onChange={setToDate}
                  width={200}
                  min={fromResolvedDate}
                  max={today}
                />

                {/* Export button — invisible label keeps it top-aligned with the other fields; extra left margin separates action from fields */}
                <div style={{ marginLeft: 12 }}>
                  <label
                    className="form-label mb-1 small"
                    style={{ visibility: "hidden" }}
                  >
                    &nbsp;
                  </label>
                  <div>
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm"
                      style={{
                        height: 36,
                        paddingLeft: 20,
                        paddingRight: 20,
                        fontWeight: 600,
                      }}
                      disabled={!selectedIds.length || triggering}
                    >
                      {exportSubmitButtonLabel({
                        triggering,
                        selectedCount: selectedIds.length,
                        usesSyncDownload,
                        usesAsyncQueue,
                      })}
                    </button>
                  </div>
                </div>
              </div>

              {/* Async-only notice */}
              {asyncOnlyDescriptors.length > 0 && (
                <div className="alert alert-info py-2 small mt-3 mb-0">
                  <p className="mb-0">
                    ⓘ Data export, when initiated, will run in the background.
                    You&apos;ll receive an email when your files are ready.
                    Results will appear in the Export History below.
                  </p>
                  {showDateScopeNote && (
                    <>
                      <p className="mb-0 mt-1">
                        <strong>Selected date range applies to:</strong>{" "}
                        {dateApplicableDescriptors
                          .map(exportTypeLabel)
                          .join(", ")}
                      </p>
                      <p className="mb-0 mt-1">
                        For other export types, the date range is not
                        applicable, so all data will be exported.
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Error / in-progress warning */}
              {triggerError && (
                <div
                  className={`alert py-2 small mt-3 mb-0 ${
                    triggerError
                      .toLowerCase()
                      .includes("already in progress") ||
                    triggerError.toLowerCase().includes("already in queue")
                      ? "alert-warning"
                      : "alert-danger"
                  }`}
                >
                  {triggerError}
                </div>
              )}
            </form>
          </div>
        </div>

        {/* ── Export History ───────────────────────────────────────────── */}
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span className="fw-semibold">Export History</span>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => refetchHistory()}
            >
              ↻ Refresh
            </button>
          </div>
          <div className="card-body p-0">
            {historyLoading ? (
              <p className="text-muted p-3 mb-0">Loading history…</p>
            ) : history.length === 0 ? (
              <p className="text-muted p-3 mb-0">No exports yet.</p>
            ) : (
              <div className="table-responsive">
                <table
                  className="table table-hover table-sm mb-0"
                  style={{ borderCollapse: "collapse" }}
                >
                  <thead>
                    <tr
                      style={{
                        background: "#007d7d",
                        color: "#fff",
                        fontWeight: 600,
                        borderLeft: "none",
                        lineHeight: "1.4",
                      }}
                    >
                      <th style={{ whiteSpace: "nowrap" }}>Job ID ↓</th>
                      <th style={{ whiteSpace: "nowrap" }}>Correlation ID</th>
                      <th>Export</th>
                      <th>Exported By</th>
                      <th>Started</th>
                      <th>Completed</th>
                      <th>Status</th>
                      <th>Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((item) => (
                      <tr
                        key={item.id}
                        style={{ borderLeft: "none", lineHeight: "1.4" }}
                      >
                        <td
                          className="align-middle text-muted small font-monospace"
                          style={{ padding: "8px 10px", whiteSpace: "nowrap" }}
                        >
                          #{item.id}
                        </td>
                        <td
                          className="align-middle"
                          style={{ padding: "8px 10px" }}
                        >
                          <span
                            className="font-monospace"
                            style={{
                              fontSize: "0.7rem",
                              color: "#6c757d",
                              letterSpacing: "-0.01em",
                              cursor: "pointer",
                              userSelect: "all",
                            }}
                            title="Click to select and copy for log search"
                          >
                            {item.correlationId ?? "—"}
                          </span>
                        </td>
                        <td
                          className="align-middle"
                          style={{ padding: "8px 10px" }}
                        >
                          {(() => {
                            const full = exportLabelForId(
                              item.exportId,
                              item.label
                            );
                            const parts = full.split(", ");
                            if (parts.length <= 2) return full;
                            return (
                              <span
                                className="DataExportName"
                                data-tooltip={full}
                              >
                                {parts.slice(0, 2).join(", ")}…
                              </span>
                            );
                          })()}
                        </td>
                        <td
                          className="align-middle text-muted small"
                          style={{ padding: "8px 10px", whiteSpace: "nowrap" }}
                        >
                          {item.exportedByEmail ?? "—"}
                        </td>
                        <td
                          className="align-middle text-muted small"
                          style={{ padding: "8px 10px", whiteSpace: "nowrap" }}
                        >
                          {formatDate(item.createdAt)}
                        </td>
                        <td
                          className="align-middle text-muted small"
                          style={{ padding: "8px 10px", whiteSpace: "nowrap" }}
                        >
                          {item.status === "processing" ||
                          item.status === "queued"
                            ? "—"
                            : formatDate(item.updatedAt)}
                        </td>
                        <td
                          className="align-middle"
                          style={{ padding: "8px 10px", whiteSpace: "nowrap" }}
                        >
                          <span
                            className={`badge ${
                              STATUS_CLASS[item.status] ?? ""
                            }`}
                            style={
                              item.status === "processing" ||
                              item.status === "queued"
                                ? {
                                    backgroundColor: "#f59e0b",
                                    color: "#1a1a1a",
                                    padding: "3px 8px",
                                    borderRadius: 4,
                                  }
                                : undefined
                            }
                          >
                            {STATUS_LABEL[item.status] ?? item.status}
                          </span>
                          {(item.status === "processing" ||
                            item.status === "queued") && (
                            <button
                              title="Cancel this job"
                              disabled={cancellingIds.has(item.id)}
                              onClick={() => handleCancel(item.id)}
                              style={{
                                marginLeft: 6,
                                background: "none",
                                border: "none",
                                padding: "0 2px",
                                cursor: "pointer",
                                color: "#dc3545",
                                fontSize: "0.9em",
                                lineHeight: 1,
                                verticalAlign: "middle",
                                opacity: cancellingIds.has(item.id) ? 0.4 : 1,
                              }}
                            >
                              {cancellingIds.has(item.id) ? "…" : "✕"}
                            </button>
                          )}
                        </td>
                        <td
                          className="align-middle"
                          style={{ padding: "8px 10px" }}
                        >
                          <DownloadCell item={item} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hidden pollers — one per active async job */}
      {Array.from(activePolls.entries()).map(([jobId, label]) => (
        <JobPollerRow
          key={jobId}
          jobId={jobId}
          label={label}
          onComplete={onPollComplete}
        />
      ))}

      {progressModalVisible ? (
        <Modal
          small
          onClose={() => setProgressModalVisible(false)}
          testId="export-in-progress-modal"
        >
          <Modal.Header>Your Data Export is in Progress</Modal.Header>
          <Modal.Content>
            <p>
              This export is in progress. Please check back shortly for your
              exported data. An email confirmation will be sent when ready.
            </p>
            {progressDateApplicableNames.length > 0 && (
              <p className="mb-1">
                <strong>Selected date range applies to:</strong>{" "}
                {progressDateApplicableNames.join(", ")}
              </p>
            )}
            {progressDateApplicableNames.length > 0 &&
              progressAllDataNames.length > 0 && (
                <p className="mb-1">
                  For other export types, the date range is not applicable, so
                  all data will be exported.
                </p>
              )}
            {progressJobIds.length > 0 && (
              <p className="mb-0">Job ID #: {progressJobIds.join(", ")}</p>
            )}
          </Modal.Content>
          <Modal.Actions>
            <ButtonComponent
              primary
              fullWidth
              onClick={() => setProgressModalVisible(false)}
            >
              Close
            </ButtonComponent>
          </Modal.Actions>
        </Modal>
      ) : null}
    </Fragment>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Invisible polling component — one instance per active job
// ─────────────────────────────────────────────────────────────────────────────

type JobPollerRowProps = {
  jobId: number;
  label: string;
  onComplete: (result: DataExportJobStatus, label: string) => void;
};

function JobPollerRow({ jobId, label, onComplete }: JobPollerRowProps) {
  const handleComplete = useCallback(
    (result: DataExportJobStatus) => onComplete(result, label),
    [onComplete, label]
  );
  const { startPolling, stopPolling } = useDataExportJobPoller(
    jobId,
    handleComplete
  );

  useEffect(() => {
    startPolling(jobId);
    return () => stopPolling();
  }, [jobId, startPolling, stopPolling]);

  return null;
}

export default DataExports;
